import { useState, useMemo, useEffect, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InventoryIcon from '@mui/icons-material/Inventory';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import axiosInstance from '../../lib/axios';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import { getUser } from '../../utils/auth';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { ResponsiveTable, Column } from '../../components/mobile/ResponsiveTable';
import {
  PaymentStatusChip,
  FulfillmentStatusChip,
  OrderTypeBadge,
} from '../../components/orders';
import {
  Order,
  OrderType,
  OrderPaymentStatus,
  OrderFulfillmentStatus,
} from '../../types';

const fetchOrders = async (locationId: string | null, page: number, pageSize: number) => {
  const params: Record<string, any> = {
    page: page + 1,
    limit: pageSize,
    includeCustomer: true,
    includeItems: true,
    includeReseller: true,
  };

  // Only include locationId if it's set (non-admin users)
  if (locationId) {
    params.locationId = locationId;
  }

  const response = await axiosInstance.get(`/orders`, { params });

  return {
    data: Array.isArray(response.data) ? response.data : response.data.data || [],
    total: response.data.total || (Array.isArray(response.data) ? response.data.length : 0),
  };
};

const filterOptions: FilterOption[] = [
  {
    field: 'orderType',
    label: 'Order Type',
    type: 'select',
    options: [
      { value: OrderType.B2C, label: 'B2C' },
      { value: OrderType.B2B_DIRECT, label: 'B2B Direct' },
      { value: OrderType.B2B_QUOTE, label: 'B2B Quote' },
    ],
  },
  {
    field: 'paymentStatus',
    label: 'Payment Status',
    type: 'select',
    options: [
      { value: OrderPaymentStatus.UNPAID, label: 'Unpaid' },
      { value: OrderPaymentStatus.PARTIALLY_PAID, label: 'Partially Paid' },
      { value: OrderPaymentStatus.PAID, label: 'Paid' },
      { value: OrderPaymentStatus.REFUNDED_PARTIAL, label: 'Partial Refund' },
      { value: OrderPaymentStatus.REFUNDED_FULL, label: 'Refunded' },
    ],
  },
  {
    field: 'fulfillmentStatus',
    label: 'Fulfillment Status',
    type: 'select',
    options: [
      { value: OrderFulfillmentStatus.PENDING, label: 'Pending' },
      { value: OrderFulfillmentStatus.CONFIRMED, label: 'Confirmed' },
      { value: OrderFulfillmentStatus.READY_FOR_PICKUP, label: 'Ready for Pickup' },
      { value: OrderFulfillmentStatus.READY_FOR_DELIVERY, label: 'Ready for Delivery' },
      { value: OrderFulfillmentStatus.OUT_FOR_DELIVERY, label: 'Out for Delivery' },
      { value: OrderFulfillmentStatus.DELIVERED, label: 'Delivered' },
      { value: OrderFulfillmentStatus.PICKED_UP, label: 'Picked Up' },
      { value: OrderFulfillmentStatus.COMPLETED, label: 'Completed' },
      { value: OrderFulfillmentStatus.CANCELLED, label: 'Cancelled' },
    ],
  },
  {
    field: 'paymentMethod',
    label: 'Payment Method',
    type: 'select',
    options: [
      { value: 'cash', label: 'Cash' },
      { value: 'card', label: 'Card' },
      { value: 'mobile', label: 'Mobile' },
    ],
  },
];

const exportColumns: ColumnConfig[] = [
  { field: 'trackingNumber', headerName: 'Tracking #' },
  {
    field: 'orderType',
    headerName: 'Type',
    valueFormatter: (value) => {
      switch (value) {
        case OrderType.B2C: return 'B2C';
        case OrderType.B2B_DIRECT: return 'B2B Direct';
        case OrderType.B2B_QUOTE: return 'B2B Quote';
        default: return value;
      }
    },
  },
  {
    field: 'customerName',
    headerName: 'Customer',
    valueFormatter: (_, row) => {
      if (row.orderType !== OrderType.B2C && row.reseller) {
        return row.reseller.companyName ||
          (row.reseller.user ? `${row.reseller.user.firstName} ${row.reseller.user.lastName}` : 'B2B Customer');
      }
      if (row.customer) {
        return `${row.customer.firstName} ${row.customer.lastName}`;
      }
      return row.customerName || 'Walk-in';
    },
  },
  {
    field: 'totalAmount',
    headerName: 'Total',
    valueFormatter: (value) => {
      const num = Number(value) || 0;
      return `${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DZD`;
    },
  },
  {
    field: 'paymentStatus',
    headerName: 'Payment',
    valueFormatter: (value) => {
      switch (value) {
        case OrderPaymentStatus.UNPAID: return 'Unpaid';
        case OrderPaymentStatus.PARTIALLY_PAID: return 'Partially Paid';
        case OrderPaymentStatus.PAID: return 'Paid';
        case OrderPaymentStatus.REFUNDED_PARTIAL: return 'Partial Refund';
        case OrderPaymentStatus.REFUNDED_FULL: return 'Refunded';
        default: return value;
      }
    },
  },
  {
    field: 'fulfillmentStatus',
    headerName: 'Fulfillment',
    valueFormatter: (value) => {
      switch (value) {
        case OrderFulfillmentStatus.PENDING: return 'Pending';
        case OrderFulfillmentStatus.CONFIRMED: return 'Confirmed';
        case OrderFulfillmentStatus.READY_FOR_PICKUP: return 'Ready for Pickup';
        case OrderFulfillmentStatus.COMPLETED: return 'Completed';
        case OrderFulfillmentStatus.CANCELLED: return 'Cancelled';
        default: return value;
      }
    },
  },
  {
    field: 'createdAt',
    headerName: 'Date',
    valueFormatter: (value) => new Date(value).toLocaleString('fr-DZ'),
  },
];

export const OrdersList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mode } = useContext(ColorModeContext);
  const user = getUser();
  const locationId = user?.locationId;
  const animations = useAnimationStyles();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  // Read filters from URL query parameters on mount
  useEffect(() => {
    const orderTypeParam = searchParams.get('orderType');
    const paymentStatusParam = searchParams.get('paymentStatus');
    const fulfillmentStatusParam = searchParams.get('fulfillmentStatus');

    const newFilters: Record<string, string> = {};
    if (orderTypeParam) newFilters.orderType = orderTypeParam;
    if (paymentStatusParam) newFilters.paymentStatus = paymentStatusParam;
    if (fulfillmentStatusParam) newFilters.fulfillmentStatus = fulfillmentStatusParam;

    if (Object.keys(newFilters).length > 0) {
      setActiveFilters(prev => ({ ...prev, ...newFilters }));
    }
  }, [searchParams]);

  const { data, isLoading } = useQuery({
    queryKey: ['orders', locationId, page, pageSize],
    queryFn: () => fetchOrders(locationId || null, page, pageSize),
  });

  const filteredData = useMemo(() => {
    if (!data?.data) return [];

    return data.data.filter((item: Order) => {
      // Search filter
      if (searchValue) {
        const search = searchValue.toLowerCase();
        const customerName = item.customer
          ? `${item.customer.firstName} ${item.customer.lastName}`
          : item.customerName || 'Walk-in';
        const resellerName = item.reseller?.companyName || '';
        const matchesSearch =
          item.trackingNumber?.toLowerCase().includes(search) ||
          item.receiptNumber?.toLowerCase().includes(search) ||
          customerName.toLowerCase().includes(search) ||
          resellerName.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Order type filter
      if (activeFilters.orderType && item.orderType !== activeFilters.orderType) {
        return false;
      }

      // Payment status filter
      if (activeFilters.paymentStatus && item.paymentStatus !== activeFilters.paymentStatus) {
        return false;
      }

      // Fulfillment status filter
      if (activeFilters.fulfillmentStatus && item.fulfillmentStatus !== activeFilters.fulfillmentStatus) {
        return false;
      }

      // Payment method filter
      if (activeFilters.paymentMethod && item.paymentMethod !== activeFilters.paymentMethod) {
        return false;
      }

      return true;
    });
  }, [data?.data, searchValue, activeFilters]);

  const paginatedData = useMemo(() => {
    const startIndex = page * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, page, pageSize]);

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('fr-DZ', { day: '2-digit', month: 'short', year: 'numeric' }),
      time: date.toLocaleTimeString('fr-DZ', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const total = filteredData.length;
    const totalRevenue = filteredData.reduce((sum: number, item: Order) => sum + (item.totalAmount || 0), 0);
    const b2cOrders = filteredData.filter((item: Order) => item.orderType === OrderType.B2C).length;
    const b2bOrders = filteredData.filter((item: Order) =>
      item.orderType === OrderType.B2B_DIRECT || item.orderType === OrderType.B2B_QUOTE
    ).length;
    const paidOrders = filteredData.filter((item: Order) => item.paymentStatus === OrderPaymentStatus.PAID).length;
    const pendingOrders = filteredData.filter((item: Order) =>
      item.fulfillmentStatus === OrderFulfillmentStatus.PENDING
    ).length;
    const inProgressOrders = filteredData.filter((item: Order) =>
      item.fulfillmentStatus !== OrderFulfillmentStatus.PENDING &&
      item.fulfillmentStatus !== OrderFulfillmentStatus.COMPLETED &&
      item.fulfillmentStatus !== OrderFulfillmentStatus.CANCELLED &&
      item.fulfillmentStatus !== OrderFulfillmentStatus.EXPIRED
    ).length;

    // Calculate total profit from order items margins
    const totalProfit = filteredData.reduce((sum: number, order: Order) => {
      const orderProfit = order.items?.reduce((itemSum: number, item) =>
        itemSum + (item.margin || 0), 0) || 0;
      return sum + orderProfit;
    }, 0);

    return { total, totalRevenue, totalProfit, b2cOrders, b2bOrders, paidOrders, pendingOrders, inProgressOrders };
  }, [filteredData]);

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  // Column definitions for ResponsiveTable
  const tableColumns: Column<Order>[] = [
    {
      field: 'trackingNumber',
      headerName: 'Tracking #',
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon sx={{ fontSize: 18, color: palette.primary }} />
          <Typography
            variant="body2"
            sx={{
              fontFamily: typography.fontFamily.mono,
              color: colors.textPrimary,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.bold,
              letterSpacing: typography.letterSpacing.wide,
            }}
          >
            {value || row.id?.substring(0, 8).toUpperCase()}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'orderType',
      headerName: 'Type',
      render: (value) => <OrderTypeBadge orderType={value as OrderType} />,
    },
    {
      field: 'customer',
      headerName: 'Customer',
      render: (_, row) => {
        const isB2B = row.orderType !== OrderType.B2C;
        const displayName = isB2B
          ? row.reseller?.companyName ||
            (row.reseller?.user
              ? `${row.reseller.user.firstName} ${row.reseller.user.lastName}`
              : 'B2B Customer')
          : row.customer
            ? `${row.customer.firstName} ${row.customer.lastName}`
            : row.customerName || 'Walk-in';

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isB2B ? (
              <BusinessIcon sx={{ fontSize: 18, color: palette.accent }} />
            ) : (
              <PersonIcon sx={{ fontSize: 18, color: palette.info }} />
            )}
            <Typography
              variant="body2"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.semibold,
                color: colors.textPrimary,
                fontSize: typography.fontSize.sm,
              }}
            >
              {displayName}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'totalAmount',
      headerName: 'Total',
      render: (value) => (
        <Box
          sx={{
            display: 'inline-flex',
            px: 2,
            py: 0.75,
            borderRadius: theme.radius.xs,
            backgroundColor: alpha(palette.success, 0.1),
            border: `1px solid ${palette.success}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.extrabold,
              color: palette.success,
            }}
          >
            {formatCurrency(value || 0)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'paymentStatus',
      headerName: 'Payment',
      render: (value) => <PaymentStatusChip status={value as OrderPaymentStatus} />,
    },
    {
      field: 'fulfillmentStatus',
      headerName: 'Fulfillment',
      render: (value) => <FulfillmentStatusChip status={value as OrderFulfillmentStatus} />,
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      render: (value) => {
        const orderDate = formatDateTime(value);
        return (
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.semibold,
                color: colors.textPrimary,
                fontSize: typography.fontSize.sm,
                lineHeight: 1.2,
              }}
            >
              {orderDate.date}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: typography.fontFamily.mono,
                color: colors.textMuted,
                fontSize: typography.fontSize.xs,
              }}
            >
              {orderDate.time}
            </Typography>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Industrial Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <InventoryIcon sx={{ color: palette.primary, fontSize: 24 }} />
              <Typography
                variant="overline"
                sx={{
                  color: palette.primary,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                ORDER MANAGEMENT
              </Typography>
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              ORDERS
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/app/pos')}
            sx={{
              backgroundColor: palette.primary,
              color: '#FFFFFF',
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
              fontSize: typography.fontSize.sm,
              px: 3,
              py: 1.5,
              borderRadius: theme.radius.sm,
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': {
                backgroundColor: alpha(palette.primary, 0.8),
                transform: 'translateY(-2px) scale(1.02)',
                boxShadow: `0 8px 24px ${alpha(palette.primary, 0.3)}`,
              },
              '&:active': {
                transform: 'scale(0.98)',
              },
            }}
          >
            New Order
          </Button>
        </Box>
      </Box>

      {/* Stats Dashboard */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3, ...animations.slideUp(0.1) }}>
        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.primary,
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.primary, 0.15)}`,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                TOTAL ORDERS
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: colors.textPrimary,
                  lineHeight: 1,
                  mt: 1,
                }}
              >
                {stats.total}
              </Typography>
            </Box>
            <ShoppingCartIcon sx={{ fontSize: 40, color: alpha(palette.primary, 0.2) }} />
          </Box>
        </Card>

        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.success,
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.success, 0.15)}`,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                REVENUE
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: palette.success,
                  lineHeight: 1,
                  mt: 1,
                  fontSize: typography.fontSize.lg,
                }}
              >
                {formatCurrency(stats.totalRevenue)}
              </Typography>
            </Box>
            <TrendingUpIcon sx={{ fontSize: 40, color: alpha(palette.success, 0.2) }} />
          </Box>
        </Card>

        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.warning,
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.warning, 0.15)}`,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                PENDING
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: palette.warning,
                  lineHeight: 1,
                  mt: 1,
                }}
              >
                {stats.pendingOrders}
              </Typography>
            </Box>
            <HourglassEmptyIcon sx={{ fontSize: 40, color: alpha(palette.warning, 0.2) }} />
          </Box>
        </Card>

        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.info,
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.info, 0.15)}`,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                IN PROGRESS
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: palette.info,
                  lineHeight: 1,
                  mt: 1,
                }}
              >
                {stats.inProgressOrders}
              </Typography>
            </Box>
            <LocalShippingIcon sx={{ fontSize: 40, color: alpha(palette.info, 0.2) }} />
          </Box>
        </Card>

        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.success,
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.success, 0.15)}`,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                PAID
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: palette.success,
                  lineHeight: 1,
                  mt: 1,
                }}
              >
                {stats.paidOrders}
              </Typography>
            </Box>
            <CheckCircleIcon sx={{ fontSize: 40, color: alpha(palette.success, 0.2) }} />
          </Box>
        </Card>

        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.accent,
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.accent, 0.15)}`,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                B2B
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: palette.accent,
                  lineHeight: 1,
                  mt: 1,
                }}
              >
                {stats.b2bOrders}
              </Typography>
            </Box>
            <BusinessIcon sx={{ fontSize: 40, color: alpha(palette.accent, 0.2) }} />
          </Box>
        </Card>
      </Box>

      {/* Search & Filter Bar */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          p: 2,
          mb: 3,
          ...animations.slideUp(0.15),
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="SEARCH ORDERS..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 300,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                '& fieldset': { border: 'none' },
                '&:hover': {
                  borderColor: colors.borderLight,
                },
                '&.Mui-focused': {
                  borderColor: palette.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
                fontWeight: typography.fontWeight.medium,
                textTransform: 'uppercase',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
          />
          <TableToolbar
            title=""
            searchValue=""
            onSearchChange={() => {}}
            searchPlaceholder=""
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={exportColumns}
            exportFileName="orders"
          />
        </Box>
      </Card>

      {/* Orders Table */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          ...animations.slideUp(0.2),
        }}
      >
        <ResponsiveTable
          columns={tableColumns}
          rows={paginatedData}
          loading={isLoading}
          emptyMessage="No orders found"
          primaryField="trackingNumber"
          getPrimaryText={(row) => row.trackingNumber || `Order #${row.id?.slice(0, 8) || 'Unknown'}`}
          onRowClick={(order) => navigate(`/app/orders/show/${order.id}`)}
        />

        {/* Industrial Pagination */}
        <Box
          sx={{
            backgroundColor: colors.surfaceElevated,
            borderTop: `2px solid ${colors.border}`,
          }}
        >
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              '& .MuiTablePagination-toolbar': {
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.mono,
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                letterSpacing: typography.letterSpacing.wide,
              },
              '& .MuiSelect-select': {
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.bold,
              },
            }}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default OrdersList;
